import { Button, Container, Grid, Snackbar } from '@material-ui/core'
import { Info } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { getActivityByUserID } from '../../actions/activityActions'
import {
  archiveProject,
  deleteProject,
  getProjectsByUserId,
} from '../../actions/projectActions'
import { SIDE_NAV_CLOSE } from '../../constants/navigationConstants'
import {
  PROJECT_ARCHIVE_RESET,
  PROJECT_CURRENT_STEP,
  PROJECT_DELETE_RESET,
} from '../../constants/projectConstants'
import Loader from '../Loader/Loader'
import Message from '../Message/Message'
import ModalPopUp from '../ModalPopUp/ModalPopUp'
import ToastMessage from '../ToastMessage/ToastMessage'
import Activity from './Activity/Activity'
import PendingRequests from './PendingRequest/PendingRequests'
import RecentProjects from './RecentProjects/RecentProjects'

const DashboardWrapper = styled.div`
  &.dashboard {
    .pending-requests-table {
      max-height: 525px;
      min-height: 525px;
      background: white;
    }
  }
`

const Dashboard = ({ projectNotFound = false }) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const [projectToDelete, setProjectToDelete] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteToast, setShowDeleteToast] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(projectNotFound)

  const { loading, error, success, projects } = useSelector(
    (state) => state.projectList
  )
  const { userInfo } = useSelector((state) => state.userLogin)
  const { success: archiveSuccess } = useSelector(
    (state) => state.projectArchive
  )

  const {
    loading: deleteLoading,
    error: deleteError,
    success: successDelete,
  } = useSelector((state) => state.projectDelete)

  const pendingProjects = projects
    ? projects.filter(
        (x) =>
          x.status === 'New' ||
          x.status === 'Submitted for review' ||
          x.status === 'Quote submitted'
      )
    : []

  const activeProjects = projects
    ? projects.filter((x) => x.status !== 'Archived')
    : []

  useEffect(() => {
    if (archiveSuccess) {
      dispatch(getProjectsByUserId(userInfo._id))
      dispatch(getActivityByUserID())
      dispatch({ type: PROJECT_ARCHIVE_RESET })
      setShowDeleteModal(false)
    }
  }, [archiveSuccess, dispatch, userInfo])

  useEffect(() => {
    dispatch(getProjectsByUserId(userInfo._id))
    dispatch(getActivityByUserID())
  }, [dispatch, userInfo])

  useEffect(() => {
    if (successDelete) {
      setShowDeleteModal(false)
      setShowDeleteToast(true)
      dispatch({ type: PROJECT_DELETE_RESET })
      dispatch(getProjectsByUserId(userInfo._id))
      dispatch({ type: SIDE_NAV_CLOSE })

      window.scrollTo(0, 0)
    }
  }, [dispatch, successDelete, userInfo])

  const handleDeleteClick = (project) => {
    setProjectToDelete(project)
    setShowDeleteModal(true)
  }

  const handleEditViewClick = (project) => {
    if (project.status === 'New') {
      if (project.requests.length) {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 5,
        })
      } else {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 2,
        })
      }
      history.push(`/project/edit/${project._id}`)
    } else {
      history.push(`/myquality/project/${project._id}`)
    }
  }

  const confirmDeleteProject = () => {
    // If user is an admin, allow them to delete
    // If user is a regular user, archive the project and do not display it to them
    if (userInfo.isAdmin) {
      dispatch(deleteProject(projectToDelete._id))
    } else {
      let projectClone = { ...projectToDelete }

      projectClone.archiveReason = 'User archived project'
      projectClone.status = 'Archived'

      dispatch(archiveProject(projectClone))
    }
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <DashboardWrapper className="dashboard gray-bg">
      <Container className="p-b-100">
        <h3 className="page-title accent-left">Dashboard</h3>

        {loading && <Loader />}

        {error && <Message variant="error">{error}</Message>}

        {success && projects && projects.length === 0 && (
          <div className="flex flex-column align-center m-t-150">
            <Info
              className="m-b-35"
              style={{ fontSize: 42, color: '#cacaca' }}
            />
            <h5 className="gray-text fs18 m-b-32">
              No recent projects. Add a quote to get started.
            </h5>
            <Button
              className="btn btn-gradient"
              onClick={() => history.push('/createaquote')}
            >
              Start a quote
            </Button>
          </div>
        )}

        {!loading && !error && projects && projects.length > 0 && (
          <Grid container spacing={6}>
            <Grid item md={8}>
              {activeProjects.length > 0 && (
                <RecentProjects
                  projects={activeProjects}
                  handleDeleteClick={handleDeleteClick}
                  handleEditViewClick={handleEditViewClick}
                />
              )}

              {pendingProjects.length > 0 && (
                <PendingRequests
                  projects={pendingProjects}
                  handleDeleteClick={handleDeleteClick}
                  handleEditViewClick={handleEditViewClick}
                />
              )}
            </Grid>
            <Grid item md={4}>
              <Activity />
            </Grid>
          </Grid>
        )}
      </Container>

      <ModalPopUp
        title="Delete project"
        setShowModal={setShowDeleteModal}
        showModal={showDeleteModal}
      >
        <h4 className="m-b-32">Delete project?</h4>
        <p>
          Are you sure you want to delete {projectToDelete.name}? By deleting
          this project, you will lose all previous activity communications about
          the project.
        </p>

        {deleteError && <Message variant="danger">{deleteError}</Message>}

        <div className="actions m-t-40 flex-end flex">
          <Button
            color="primary"
            variant="outlined"
            className="m-r-24"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmDeleteProject}
            disabled={deleteLoading}
          >
            Confirm
          </Button>
        </div>
      </ModalPopUp>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Project deleted</p>
            <p className="fs14 m-b-0">
              You successfully deleted {projectToDelete.name}.
            </p>
          </>
        }
        open={showDeleteToast}
        setOpen={setShowDeleteToast}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message="Project does not exist."
        key={'top center'}
        className="warning"
      />
    </DashboardWrapper>
  )
}

export default Dashboard
